<template>
  <div class="contact-page">
    <a-form layout="inline" class="contact-filter">
      <a-form-item>
        <a-input v-model="form.keyword" placeholder="搜索用户名/邮箱/手机号/钉钉ID" style="width: 300px" allow-clear @pressEnter="search" />
      </a-form-item>
      <a-form-item>
        <a-select v-model="form.ding_active" placeholder="在职状态" allow-clear style="width: 150px" @change="search">
          <a-select-option :value="1">在职</a-select-option>
          <a-select-option :value="0">离职</a-select-option>
          <a-select-option :value="2">同步中</a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item>
        <a-select v-model="form.role" placeholder="角色" allow-clear style="width: 150px" @change="search">
          <a-select-option value="admin">管理员</a-select-option>
          <a-select-option value="dev">开发者</a-select-option>
          <a-select-option value="user">普通用户</a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item>
        <a-input v-model="form.department" placeholder="部门" allow-clear style="width: 200px" @pressEnter="search" />
      </a-form-item>
      <a-form-item>
        <a-button type="primary" @click="search">搜索</a-button>
        <a-button @click="reset" style="margin-left: 10px">重置</a-button>
      </a-form-item>
      <a-form-item style="margin-left: auto">
        <a-button v-permission="PERMISSIONS.CONTACT_SYNC" type="primary" @click="handleSync" :loading="syncLoading"> <a-icon type="sync" /> 同步钉钉 </a-button>
        <a-button v-permission="PERMISSIONS.CONTACT_EXPORT" @click="handleExport" :loading="exportLoading" style="margin-left: 10px">
          <a-icon type="download" /> 导出
        </a-button>
      </a-form-item>
    </a-form>

    <div class="table-wrapper">
      <a-table
        bordered
        :data-source="dataSource"
        :columns="columns"
        :pagination="false"
        :loading="loading"
        :rowKey="record => record.id"
        :scroll="{ y: tableScrollY }"
      >
        <template slot="ding_active" slot-scope="ding_active">
          <a-tag :color="dingActiveColor(ding_active)">
            {{ dingActiveText(ding_active) }}
          </a-tag>
        </template>

        <template slot="dingtalk_id" slot-scope="dingtalk_id">
          <div class="copy-cell">
            <span class="ellipsis-1" :title="dingtalk_id">{{ dingtalk_id || '-' }}</span>
            <a-icon v-if="dingtalk_id" type="copy" class="copy-icon" @click="$copy(dingtalk_id)" />
          </div>
        </template>

        <template slot="department" slot-scope="department">
          <span class="ellipsis-1" :title="department">{{ department || '-' }}</span>
        </template>

        <template slot="email" slot-scope="email">
          <div class="copy-cell">
            <span class="ellipsis-1" :title="email">{{ email || '-' }}</span>
            <a-icon v-if="email" type="copy" class="copy-icon" @click="$copy(email)" />
          </div>
        </template>

        <template slot="phone" slot-scope="phone">
          <div class="copy-cell">
            <span class="ellipsis-1" :title="phone">{{ phone || '-' }}</span>
            <a-icon v-if="phone" type="copy" class="copy-icon" @click="$copy(phone)" />
          </div>
        </template>

        <template slot="hired_date" slot-scope="hired_date">
          {{ hired_date ? formatDate(hired_date) : '-' }}
        </template>

        <template slot="role" slot-scope="role, record">
          <div v-if="canEditRole && editingRow === record.username">
            <a-select v-model="editingRole" style="width: 100px" size="small" @change="handleRoleChange(record.username)">
              <a-select-option value="admin">管理员</a-select-option>
              <a-select-option value="dev">开发者</a-select-option>
              <a-select-option value="user">普通用户</a-select-option>
            </a-select>
            <a-button size="small" type="link" @click="cancelEdit">取消</a-button>
          </div>
          <div v-else>
            <a-tag :color="roleColor(role)">{{ roleText(role) }}</a-tag>
            <a-icon v-if="canEditRole" type="edit" style="margin-left: 6px; color: #1890ff; cursor: pointer" @click="startEdit(record)" />
          </div>
        </template>

        <template slot="action" slot-scope="_, record">
          <div class="button-box">
            <a-button @click="handleViewDetail(record)">详情</a-button>
          </div>
        </template>
      </a-table>
    </div>

    <div class="contact-pagination">
      <a-pagination
        v-model="currentPage"
        :page-size-options="pageSizeOptions"
        :total="total"
        :page-size="pageSize"
        :showSizeChanger="true"
        :showQuickJumper="true"
        :show-total="total => `共 ${total} 条`"
        @change="pageChange"
        @showSizeChange="pageChange"
      />
    </div>

    <a-modal v-model="syncModalVisible" title="同步钉钉通讯录" :footer="null" width="500px">
      <div v-if="syncRecord">
        <p><strong>上次同步时间：</strong>{{ syncRecord.last_sync_time || '无记录' }}</p>
        <p><strong>同步结果：</strong>{{ syncRecord.result || '无记录' }}</p>
      </div>
      <div v-else>
        <p>暂无同步记录</p>
      </div>
    </a-modal>

    <a-modal v-model="detailVisible" title="员工详情" :footer="null" width="450px">
      <a-spin :spinning="detailLoading">
        <div class="detail-body">
          <template v-if="detailUser">
            <a-descriptions bordered :column="1" size="small">
              <a-descriptions-item label="用户名">{{ detailUser.username }}</a-descriptions-item>
              <a-descriptions-item label="邮箱">{{ detailUser.email || '-' }}</a-descriptions-item>
              <a-descriptions-item label="手机号">{{ detailUser.phone || '-' }}</a-descriptions-item>
              <a-descriptions-item label="钉钉ID">{{ detailUser.dingtalk_id || '-' }}</a-descriptions-item>
              <a-descriptions-item label="部门">{{ detailUser.department || '-' }}</a-descriptions-item>
              <a-descriptions-item label="入职时间">{{ detailUser.hired_date ? formatDate(detailUser.hired_date) : '-' }}</a-descriptions-item>
              <a-descriptions-item label="在职状态">
                <a-tag :color="dingActiveColor(detailUser.ding_active)">{{ dingActiveText(detailUser.ding_active) }}</a-tag>
              </a-descriptions-item>
              <a-descriptions-item label="角色">
                <a-tag :color="roleColor(detailUser.role)">{{ roleText(detailUser.role) }}</a-tag>
              </a-descriptions-item>
              <a-descriptions-item label="休假状态">
                <a-tag v-if="detailUser.is_on_leave" color="orange">{{ detailUser.leave_status || '休假中' }}</a-tag>
                <a-tag v-else-if="'is_on_leave' in detailUser" color="green">正常</a-tag>
                <a-tag v-else color="default">-</a-tag>
              </a-descriptions-item>
            </a-descriptions>
          </template>
        </div>
      </a-spin>
    </a-modal>
  </div>
</template>

<script>
import { getUserList, syncDingtalk, getSyncRecord, updateUserRole, exportUserList, getContactDetail } from '@/api/contact'
import { PERMISSIONS } from '@/utils/permission'

export default {
  data() {
    return {
      columns: [
        {
          title: '用户名',
          dataIndex: 'username',
          key: 'username',
          width: '15%'
        },
        {
          title: '邮箱',
          dataIndex: 'email',
          key: 'email',
          width: '20%',
          scopedSlots: { customRender: 'email' }
        },
        {
          title: '手机号',
          dataIndex: 'phone',
          key: 'phone',
          width: '12%',
          scopedSlots: { customRender: 'phone' }
        },
        {
          title: '钉钉ID',
          dataIndex: 'dingtalk_id',
          key: 'dingtalk_id',
          width: '15%',
          scopedSlots: { customRender: 'dingtalk_id' }
        },
        {
          title: '部门',
          dataIndex: 'department',
          key: 'department',
          width: '20%',
          scopedSlots: { customRender: 'department' }
        },
        {
          title: '在职状态',
          dataIndex: 'ding_active',
          key: 'ding_active',
          width: '8%',
          scopedSlots: { customRender: 'ding_active' }
        },
        {
          title: '入职时间',
          dataIndex: 'hired_date',
          key: 'hired_date',
          width: '10%',
          scopedSlots: { customRender: 'hired_date' }
        },
        {
          title: '角色',
          dataIndex: 'role',
          key: 'role',
          width: '12%',
          scopedSlots: { customRender: 'role' }
        },
        {
          title: '操作',
          key: 'action',
          width: '8%',
          scopedSlots: { customRender: 'action' }
        }
      ],
      form: {
        keyword: '',
        ding_active: undefined,
        role: undefined,
        department: ''
      },
      dataSource: [],
      pageSizeOptions: ['20', '50', '100'],
      currentPage: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      syncLoading: false,
      exportLoading: false,
      syncModalVisible: false,
      syncRecord: null,
      editingRow: null,
      editingRole: '',
      tableScrollY: 0,
      detailVisible: false,
      detailLoading: false,
      detailUser: null
    }
  },
  computed: {
    // 角色编辑权限：仅管理员可修改成员角色
    canEditRole() {
      return this.$hasPermission(PERMISSIONS.CONTACT_ROLE_UPDATE)
    }
  },
  created() {
    this.fetchList()
  },
  mounted() {
    this.calcTableHeight()
    window.addEventListener('resize', this.calcTableHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calcTableHeight)
  },
  methods: {
    fetchList() {
      this.loading = true
      const params = {
        page_num: this.currentPage,
        page_size: this.pageSize,
        keyword: this.form.keyword || undefined,
        ding_active: this.form.ding_active,
        role: this.form.role || undefined,
        department: this.form.department || undefined
      }
      getUserList(params)
        .then(res => {
          this.dataSource = res.data.list || []
          this.total = res.data.total || 0
        })
        .catch(() => {
          this.dataSource = []
          this.total = 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    search() {
      this.currentPage = 1
      this.fetchList()
    },
    reset() {
      this.form = {
        keyword: '',
        ding_active: undefined,
        role: undefined,
        department: ''
      }
      this.currentPage = 1
      this.fetchList()
    },
    startEdit(record) {
      this.editingRow = record.username
      this.editingRole = record.role
    },
    cancelEdit() {
      this.editingRow = null
      this.editingRole = ''
    },
    handleRoleChange(username) {
      updateUserRole({ username, role: this.editingRole })
        .then(() => {
          this.$message.success('角色更新成功', 2)
          this.editingRow = null
          this.editingRole = ''
          this.fetchList()
        })
        .catch(() => {
          this.$message.error('角色更新失败', 2)
        })
    },
    roleColor(role) {
      const map = { admin: 'red', dev: 'blue', user: 'green' }
      return map[role] || 'green'
    },
    roleText(role) {
      const map = { admin: '管理员', dev: '开发者', user: '普通用户' }
      return map[role] || role
    },
    dingActiveColor(dingActive) {
      const map = { 1: 'green', 0: 'red', 2: 'orange' }
      return map[dingActive] || 'default'
    },
    dingActiveText(dingActive) {
      const map = { 1: '在职', 0: '离职', 2: '同步中' }
      return map[dingActive] || '未知'
    },
    pageChange(page, pageSize) {
      this.currentPage = page
      this.pageSize = pageSize
      this.fetchList()
    },
    handleSync() {
      this.syncLoading = true
      syncDingtalk()
        .then(() => {
          this.$message.success('同步任务已提交，请稍后刷新查看', 3)
          this.fetchList()
        })
        .finally(() => {
          this.syncLoading = false
        })
    },
    handleExport() {
      if (this.exportLoading) return
      this.exportLoading = true
      const loading = this.$message.loading('正在导出，请稍候...', 0)
      const params = {
        keyword: this.form.keyword || undefined,
        ding_active: this.form.ding_active,
        role: this.form.role || undefined,
        department: this.form.department || undefined
      }
      exportUserList(params)
        .then(({ blob, filename }) => {
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(link.href)
          this.$message.success('导出成功', 2)
        })
        .catch(() => {
          this.$message.error('导出失败', 2)
        })
        .finally(() => {
          this.exportLoading = false
          loading()
        })
    },
    showSyncRecord() {
      getSyncRecord()
        .then(res => {
          this.syncRecord = res.data
          this.syncModalVisible = true
        })
        .catch(() => {
          this.syncRecord = null
          this.syncModalVisible = true
        })
    },
    handleViewDetail(record) {
      this.detailVisible = true
      this.detailLoading = true
      this.detailUser = null
      getContactDetail(record.username)
        .then(res => {
          this.detailUser = res.data || {}
        })
        .catch(() => {
          this.detailUser = record
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },
    calcTableHeight() {
      const pageHeight = window.innerHeight
      const headerHeight = 64
      const contentMargin = 48
      const contentPadding = 48
      const paginationHeight = 52
      const filterBox = document.querySelector('.contact-page .contact-filter')
      const filterHeight = filterBox ? filterBox.clientHeight : 52
      const tableHeaderHeight = 55
      const extraGap = 10
      this.tableScrollY = pageHeight - headerHeight - contentMargin - contentPadding - filterHeight - paginationHeight - tableHeaderHeight - extraGap
    }
  }
}
</script>

<style scoped>
.contact-page {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.contact-filter {
  margin-bottom: 10px;
  flex-shrink: 0;
}

.table-wrapper {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.contact-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  flex-shrink: 0;
}

.detail-body {
  min-height: 200px;
}

.copy-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.copy-cell .ellipsis-1 {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.button-box {
  display: flex;
  justify-content: space-evenly;
}
</style>
